import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { scheduleToday } from "@/mocks/schedule";
import { useTranslatedList } from "@/hooks/useTranslatedList";
import Reveal from "@/components/base/Reveal";

type Tab = "today" | "tomorrow" | "week";

export default function SchedulePage() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>("today");
  const locale = i18n.language === "fr" ? "fr-FR" : "en-US";

  useEffect(() => {
    document.title = "TV Schedule | L+TV TÉLÉVISION";
  }, []);

  const dayName = (offset: number) => {
    const date = new Date();
    date.setDate(date.getDate() + offset);
    return date.toLocaleDateString(locale, { weekday: "long", month: "short", day: "numeric" });
  };

  const currentHour = new Date().getHours();
  const currentMinute = new Date().getMinutes();
  const currentTime = currentHour * 60 + currentMinute;

  const isCurrentProgram = (timeStr: string) => {
    const [h, m] = timeStr.split(":").map(Number);
    const start = h * 60 + m;
    const end = start + 30;
    return currentTime >= start && currentTime < end;
  };

  const tabs = [
    { key: "today" as Tab, label: dayName(0) },
    { key: "tomorrow" as Tab, label: dayName(1) },
    { key: "week" as Tab, label: t("schedule.thisWeek") },
  ];

  const schedule = useTranslatedList(scheduleToday, ["program", "description", "category"]);

  return (
    <div className="pt-20">
      <div className="bg-primary-900 py-8">
        <div className="ltv-container">
          <Reveal direction="up">
            <h1 className="text-2xl md:text-3xl font-bold text-white">{t("schedule.title")}</h1>
            <p className="text-white/60 text-sm mt-1">{t("schedule.subtitle")}</p>
          </Reveal>
        </div>
      </div>

      <div className="bg-white border-b border-background-200">
        <div className="ltv-container py-4">
          <div className="flex gap-1 bg-background-100 rounded-md p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? "bg-primary-500 text-white"
                    : "text-foreground-600 hover:text-foreground-800"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        {activeTab === "today" && (
          <div className="space-y-2">
            {schedule.map((item, index) => {
              const current = isCurrentProgram(item.time);
              return (
                <Reveal key={item.id} direction="up" delay={Math.min(index * 30, 300)}>
                  <div
                    className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                      current
                        ? "bg-accent-50 border-accent-200"
                        : "bg-white border-background-200 hover:border-primary-200"
                    }`}
                  >
                    <div className="flex-shrink-0 w-16 text-center">
                      <p className={`font-mono text-sm font-bold ${current ? "text-accent-600" : "text-foreground-700"}`}>
                        {item.time}
                      </p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-semibold text-sm md:text-base ${current ? "text-accent-700" : "text-foreground-800"}`}>
                          {item.program}
                        </h3>
                        {item.live && (
                          <span className="ltv-live-badge text-[10px]">
                            <i className="ri-broadcast-fill"></i>
                            {t("schedule.live")}
                          </span>
                        )}
                        {current && (
                          <span className="bg-accent-500 text-white text-[10px] font-bold px-2 py-0.5 rounded">
                            {t("schedule.onNow")}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-foreground-600 mt-1">{item.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
                        <span className="bg-background-100 px-2 py-0.5 rounded">{item.category}</span>
                        <span>{item.duration}</span>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}

        {activeTab !== "today" && (
          <div className="text-center py-16">
            <i className="ri-calendar-line text-4xl text-foreground-300 mb-3"></i>
            <p className="text-foreground-500">
              {t("schedule.comingSoon", { tab: activeTab === "tomorrow" ? t("schedule.tomorrow") : t("schedule.thisWeek") })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}